import { useState, useEffect, useMemo } from "react";
import { getGeneratedCodes } from "@/services/generatedCodeService";
import { PREDEFINED_TAGS } from "@/constants/tags";

/**
 * Custom hook to get all available tags (predefined + custom from existing codes)
 * Handles loading and combining tags efficiently
 */
export const useAvailableTags = () => {
  const [customTags, setCustomTags] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadCustomTags = async () => {
      try {
        setIsLoading(true);
        const codes = await getGeneratedCodes();

        // Extract unique custom tags
        const tagsSet = new Set<string>();
        codes.forEach((code) => {
          code.tags?.forEach((tag) => {
            const trimmed = tag?.trim();
            if (trimmed) {
              tagsSet.add(trimmed);
            }
          });
        });

        setCustomTags(Array.from(tagsSet));
      } catch (error) {
        console.error("Error loading custom tags:", error);
        setCustomTags([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadCustomTags();
  }, []);

  // Combine and deduplicate predefined and custom tags
  const allTags = useMemo(() => {
    return Array.from(new Set([...PREDEFINED_TAGS, ...customTags])).sort();
  }, [customTags]);

  return { allTags, isLoading, refreshTags: () => setCustomTags([]) };
};
